import type { FunctionalComponent, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { ConsentTermsPopup } from '@/components/info/consent-terms-popup.tsx';
import { Footer } from '@/components/footer.tsx';
import { NavBack } from '@/components/nav-back.tsx';
import { MSG } from '@/lib/constants.ts';
import {
  sendMobileCode,
  submitUserInfo,
  verifyMobileCode,
  type UserInfoRequest,
} from '@/lib/user.api.ts';
import { digits, formatMinSec, formatPrice, validateBirthDate } from '@/lib/utils.ts';
import { signIn } from '@/store/auth.slice.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { DaoUser } from '@/types/dao.ts';
import type { PageProps } from '@/types/props.ts';
import {
  INFO_STEP_CODE,
  INFO_TOTAL_STEPS,
  hasReward,
  MOBILE_CODE_LENGTH,
  MOBILE_CODE_TTL_SEC,
  MOBILE_RESEND_COOLDOWN_SEC,
  infoStep,
} from '@/data/policy.ts';
import { CONSENT_DESCRIPTION, CONSENT_ITEMS, type ConsentKind } from '@/data/terms.ts';

/**
 * `?mode=edit` 이면 **수정 모드**다.
 *
 * 마이페이지 > 회원정보 수정에서 들어온 경우로, 포인트는 이미 받은 상태다.
 * 적립 문구와 완료 팝업을 빼고 세 항목을 모두 고칠 수 있게 한다.
 */
function readEditMode(): boolean {
  return new URLSearchParams(window.location.search).get('mode') === 'edit';
}

/** ?r= 복귀 주소. 내부 경로만 허용한다. */
function readReturnTo(): string {
  const raw = new URLSearchParams(window.location.search).get('r');
  if (!raw) return '/';
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
}

function formatAgreedAt(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}.${pad(now.getMonth() + 1)}.${pad(now.getDate())} ` +
    `${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

const Info: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const hydrated = useAppSelector((state) => state.auth.hydrated);

  const returnTo = useRef('/');
  const [editMode] = useState(readEditMode);
  /** 화면에 들어온 시점의 '아직 적립 안 받은 단계'. steps 와 같은 이유로 고정한다. */
  const [unclaimed, setUnclaimed] = useState<number[] | null>(null);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  /** 진행할 단계 목록. 화면 진입 시 한 번 확정하고 바꾸지 않는다. */
  const [steps, setSteps] = useState<number[] | null>(null);

  // 1단계 — 생년월일 + 성별
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [gender, setGender] = useState<'M' | 'F' | null>(null);

  // 2단계 — 휴대폰 번호
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const phone2Ref = useRef<HTMLInputElement>(null);
  const phone3Ref = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // 2단계 SMS 인증
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [mobileVerified, setMobileVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  /** 재발송까지 남은 초 */
  const [resendIn, setResendIn] = useState(0);
  /** 인증번호 유효시간 남은 초 */
  const [expiresIn, setExpiresIn] = useState(0);

  // 3단계 — 마케팅 수신 동의. 선택 항목이므로 기본은 해제다.
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreeAd, setAgreeAd] = useState(false);
  const [termsPopup, setTermsPopup] = useState<ConsentKind | null>(null);
  const [agreedAt, setAgreedAt] = useState<string | null>(null);
  /** 서버가 실제로 적립한 금액. 이미 받은 단계는 빠지므로 INFO_TOTAL_POINT 보다 작을 수 있다. */
  const [rewarded, setRewarded] = useState(0);

  useEffect(() => {
    returnTo.current = readReturnTo();
  }, []);

  // 비로그인이면 로그인 화면으로. 세션 복원이 끝난 뒤에 판단한다.
  useEffect(() => {
    if (!hydrated) return;
    if (!user?.id) route(`/login?r=${encodeURIComponent(`/info?r=${returnTo.current}`)}`, true);
  }, [hydrated, user?.id]);

  // 이미 저장된 값이 있으면 채워둔다.
  useEffect(() => {
    const birthday = String(user?.birthday ?? '');
    if (birthday.length === 8) {
      setYear(birthday.slice(0, 4));
      setMonth(birthday.slice(4, 6));
      setDay(birthday.slice(6, 8));
    }
    if (user?.gender === 'M' || user?.gender === 'F') setGender(user.gender);

    const mobile = digits(String(user?.mobile ?? ''));
    if (mobile.length >= 10) {
      setPhone1(mobile.slice(0, 3));
      setPhone2(mobile.slice(3, mobile.length - 4));
      setPhone3(mobile.slice(-4));
    }

    // 이전에 동의한 이력이 있으면 그대로 보여준다. 없으면 해제 상태 유지.
    if (user?.agreeMarketing) setAgreeMarketing(user.agreeMarketing === 'Y');
    if (user?.agreeAd) setAgreeAd(user.agreeAd === 'Y');
  }, [user]);

  const birthday = `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
  const birthValid = year.length === 4 && !!month && !!day && validateBirthDate(birthday).ok;
  const step1Valid = birthValid && !!gender;

  const mobile = `${phone1}${phone2}${phone3}`;
  const phoneFilled = /^01[0-9]\d{7,8}$/.test(mobile);

  /**
   * 이미 저장돼 있고 인증까지 끝난 번호를 그대로 두고 있는 상태.
   *
   * 번호를 바꾸지 않았으면 문자를 다시 보낼 이유가 없다. 백엔드
   * `ControllerApiUser::info()` 도 이 경우엔 세션 인증 없이 저장을 받아준다.
   * 한 자라도 고치면 아래 값이 false 가 되어 정상적으로 재인증을 요구한다.
   */
  const storedMobile = digits(String(user?.mobile ?? ''));
  const mobileUnchanged = phoneFilled && mobile === storedMobile && user?.mobileAuth === 'Y';
  /** 이번 화면에서 인증했든, 예전에 인증해 둔 번호든 — 인증이 끝난 상태 */
  const mobileDone = mobileVerified || mobileUnchanged;
  /**
   * 2단계 버튼 활성 조건. 버튼 하나가 발송 -> 확인 두 역할을 하므로 상태마다 다르다.
   *   발송 전 / 만료 : 번호가 채워졌으면
   *   발송 후        : 인증번호 6자리를 넣었으면
   */
  const step2Valid =
    mobileDone || (!codeSent || expiresIn <= 0 ? phoneFilled : code.length === MOBILE_CODE_LENGTH);

  // 적립 모드: **두 항목 모두** 동의해야 3단계 적립 버튼이 열린다.
  //   (2026-09-15 요청 — 예전에는 마케팅 활용 하나만 켜도 열렸다)
  // 수정 모드: 동의를 끄는 것(철회)도 정상 동작이므로 잠그지 않는다.
  const step3Valid = editMode || (agreeMarketing && agreeAd);

  const stepValid = [step1Valid, step2Valid, step3Valid][step - 1];

  /**
   * 1단계를 건너뛰어도 되는지.
   *
   * ★ 폼 state(step1Valid)로 판단하면 안 된다. 생년월일·성별을 채우는 prefill
   *   effect 는 첫 렌더 이후에 반영되므로, 그 시점에는 값이 비어 있어 항상
   *   "건너뛸 수 없음" 이 되고 결국 1단계부터 시작한다.
   *   회원정보(user)는 첫 렌더에 이미 있으므로 그쪽을 본다.
   *
   * 서버는 birthday/gender 를 필수로 받으므로, 완료 표시가 돼 있어도 실제 값이
   * 없으면 건너뛰지 않는다. 안 그러면 제출 단계에서 막다른 길이 된다.
   */
  const storedBirthday = digits(String(user?.birthday ?? ''));
  const canSkipStep1 =
    storedBirthday.length === 8 &&
    validateBirthDate(storedBirthday).ok &&
    (user?.gender === 'M' || user?.gender === 'F');

  /**
   * 아직 적립받지 못한 단계 번호.
   *
   * 백엔드가 포인트 원장을 보고 내려준 목록(`requestDetailSteps`)을 그대로 쓴다.
   * 값이 저장돼 있는지와는 별개다 — 이미 입력한 값은 아래 prefill 이 채워주고,
   * 사용자는 확인만 하면 된다.
   *
   * 목록이 없으면(리뉴얼 이전에 저장된 세션 등) 안전하게 전부 보여준다.
   */
  const remainSteps = steps ?? [1, 2, 3];

  /**
   * 아직 적립받지 않은 단계.
   *
   * ★ **수정 모드에서도 본다.** 마케팅 동의를 한 번도 하지 않은 회원이 '정보 수정'
   *   으로 들어와 동의하면 그것도 첫 동의라 서버가 500P 를 준다. 그런데 화면이
   *   모드만 보고 '저장하기' 라고 쓰면, 받은 줄도 모르고 넘어간다.
   *   (실제로 그렇게 보인다는 제보를 받았다)
   */
  const unclaimedSteps = unclaimed ?? [];

  /**
   * 진행할 단계 목록을 **한 번만** 확정한다.
   *
   * 단계마다 즉시 적립하므로 제출할 때마다 서버의 `requestDetailSteps` 가 줄어든다.
   * 그 값을 매 렌더 다시 계산하면 방금 끝낸 단계가 목록에서 빠져 현재 위치를 잃는다.
   * 화면에 들어온 시점의 목록을 고정해서 끝까지 그대로 쓴다.
   */
  useEffect(() => {
    if (steps || !user) return;

    // 수정 모드는 적립 여부와 무관하게 세 항목을 모두 고칠 수 있어야 한다.
    let next = editMode
      ? [1, 2, 3]
      : user.requestDetailSteps
        ? [1, 2, 3].filter((n) => user.requestDetailSteps!.includes(INFO_STEP_CODE[n]))
        : [1, 2, 3];

    setUnclaimed(
      user.requestDetailSteps
        ? [1, 2, 3].filter((n) => user.requestDetailSteps!.includes(INFO_STEP_CODE[n]))
        : [1, 2, 3]
    );

    // 1단계가 이미 적립됐더라도 서버에 보낼 생년월일·성별이 없으면 건너뛸 수 없다.
    if (!next.includes(1) && !canSkipStep1) next = [1, ...next];
    if (!next.length) next = [1, 2, 3];

    setSteps(next);
    setStep(next[0]);
    // canSkipStep1 은 user 에서 파생된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, steps]);

  // 카운트다운 (재발송 대기 / 인증번호 유효시간)
  useEffect(() => {
    if (resendIn <= 0 && expiresIn <= 0) return;

    const timer = window.setInterval(() => {
      setResendIn((v) => (v > 0 ? v - 1 : 0));
      setExpiresIn((v) => (v > 0 ? v - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendIn, expiresIn]);

  // 번호를 고치면 인증을 처음부터 다시 받는다 (시안 resetPhoneVerify 와 같은 동작).
  useEffect(() => {
    setMobileVerified(false);
    setCodeSent(false);
    setCode('');
    setResendIn(0);
    setExpiresIn(0);
  }, [mobile]);

  /**
   * 2단계 진행. 적립 버튼 하나가 두 단계를 맡는다.
   *
   *   1) 인증번호가 없거나 만료됐으면 -> 문자 발송 (이번 클릭은 여기서 끝)
   *   2) 인증번호가 있으면          -> 확인. 통과해야 다음으로 넘어간다.
   *
   * @returns 다음 단계로 진행해도 되는지
   */
  async function handleMobileStep(): Promise<boolean> {
    // 이미 인증된 번호를 그대로 두고 눌렀다면 문자를 보내지 않고 통과시킨다.
    if (mobileDone) return true;

    // --- 1) 발송
    if (!codeSent || expiresIn <= 0) {
      if (!phoneFilled) {
        await dispatch(askAlert('휴대폰 번호를 정확히 입력해 주세요.'));
        return false;
      }
      if (resendIn > 0) {
        await dispatch(askAlert(`${resendIn}초 후에 다시 시도해 주세요.`));
        return false;
      }

      setSendingCode(true);
      try {
        const response = await sendMobileCode(user?.userSeq, mobile);
        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return false;
        }

        setCodeSent(true);
        setCode('');
        setResendIn(response.data?.resendIn ?? MOBILE_RESEND_COOLDOWN_SEC);
        setExpiresIn(response.data?.expiresIn ?? MOBILE_CODE_TTL_SEC);
        codeRef.current?.focus();
      } catch {
        await dispatch(askAlert(MSG.apiFail));
      } finally {
        setSendingCode(false);
      }

      // 발송만 하고 이번 클릭은 끝. 사용자가 번호를 받아 입력해야 한다.
      return false;
    }

    // --- 2) 확인
    if (code.length < MOBILE_CODE_LENGTH) {
      await dispatch(askAlert(`문자로 받은 인증번호 ${MOBILE_CODE_LENGTH}자리를 입력해 주세요.`));
      return false;
    }

    setVerifyingCode(true);
    try {
      const response = await verifyMobileCode(user?.userSeq, mobile, code);
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return false;
      }

      setMobileVerified(true);
      setResendIn(0);
      setExpiresIn(0);
      return true;
    } catch {
      await dispatch(askAlert(MSG.apiFail));
      return false;
    } finally {
      setVerifyingCode(false);
    }
  }

  /** 숫자 입력 + maxlength 도달 시 다음 칸으로 이동 */
  function numericInput(
    setter: (v: string) => void,
    maxLength: number,
    next?: { current: HTMLInputElement | null }
  ) {
    return (e: JSX.TargetedEvent<HTMLInputElement>) => {
      const value = digits(e.currentTarget.value).slice(0, maxLength);
      setter(value);
      if (value.length >= maxLength) next?.current?.focus();
    };
  }

  function toggleAll() {
    const next = !(agreeMarketing && agreeAd);
    setAgreeMarketing(next);
    setAgreeAd(next);
  }

  function handleBack() {
    // 남은 단계 안에서만 오간다. 첫 단계에서는 화면을 빠져나간다.
    const index = remainSteps.indexOf(step);
    if (index > 0) setStep(remainSteps[index - 1]);
    else route(returnTo.current);
  }

  /** '나중에 하기' — 어느 단계에서든 그냥 빠져나간다. */
  function handleLater() {
    route(returnTo.current);
  }

  /**
   * 단계 완료 — 이번 단계의 값만 서버에 보내고 **즉시 적립**한다.
   *
   * ★ 마지막에 몰아서 보내면 안 된다. 3단계에서 동의하지 않고 '나중에 하기' 로
   *   나가는 순간 1·2단계에서 입력하고 인증까지 마친 보상이 함께 사라진다.
   *   서버도 담겨 온 항목만 검증·저장·적립하도록 맞춰져 있다.
   */
  async function handleSubmit() {
    if (!stepValid || submitting || sendingCode || verifyingCode) return;

    // 2단계는 문자 발송 / 인증번호 확인을 먼저 통과해야 한다.
    if (step === 2 && !(await handleMobileStep())) return;
    if (!user) return;

    const payload: UserInfoRequest = { userSeq: user.userSeq };
    if (step === 1) {
      payload.birthday = birthday;
      payload.gender = gender ?? 'M';
    } else if (step === 2) {
      payload.mobile = mobile;
    } else {
      payload.agreeMarketing = agreeMarketing;
      payload.agreeAd = agreeAd;
    }

    setSubmitting(true);
    try {
      const response = await submitUserInfo(payload);

      if (response.result === false) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      // 서버가 돌려준 최신 회원정보로 세션까지 갱신한다.
      // signIn 은 세션스토리지 저장과 상태 갱신을 함께 처리한다.
      // unwrap() 으로 저장 실패를 실제 예외로 만든다 (아래 catch 가 받는다).
      // ★ 병합({...user, ...next})이 아니라 교체다.
      //   백엔드는 값이 NULL 인 필드를 응답에서 **아예 빼고** 보낸다(null 로 보내지 않음).
      //   병합하면 서버에서 지워진 값(예: 마케팅 동의 철회)이 세션의 옛 값으로 되살아나
      //   영영 꺼지지 않는다. 회원 정보는 서버가 정본이므로 통째로 갈아끼운다.
      await dispatch(signIn((response.data ?? user) as DaoUser)).unwrap();

      // 단계마다 적립되므로 합산해서 마지막에 보여준다.
      const earned = rewarded + (response.rewards?.total ?? 0);
      setRewarded(earned);

      const index = remainSteps.indexOf(step);
      if (index >= 0 && index < remainSteps.length - 1) {
        setStep(remainSteps[index + 1]);
        return;
      }

      // 수정 모드라도 **이번에 적립이 있었으면 그 사실을 알린다.**
      // 조용히 저장만 하면 500P 를 받고도 받은 줄을 모른다.
      if (editMode) {
        await dispatch(
          askAlert(
            earned > 0
              ? `저장되었습니다.
${formatPrice(earned)}P가 적립되었습니다.`
              : '저장되었습니다.'
          )
        );
        route(returnTo.current);
        return;
      }

      setAgreedAt(formatAgreedAt(new Date()));
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setSubmitting(false);
    }
  }

  const isLastStep = step === remainSteps[remainSteps.length - 1];
  /**
   * 버튼 문구.
   *
   * 적립이 없는 단계에 "0P 받기" 라고 쓸 수는 없다. 정책이 바뀌어 1·2단계가
   * 0P 가 되었으므로 금액이 있는 단계에서만 금액을 적는다.
   */
  /** 이 단계에 아직 받지 않은 적립이 남았는가 */
  const stepHasReward = hasReward(step) && unclaimedSteps.includes(step);

  const submitLabel = stepHasReward
    ? step === INFO_TOTAL_STEPS
      ? `동의하고 ${formatPrice(infoStep(step).amount)}P 받기`
      : `${formatPrice(infoStep(step).amount)}P 받기`
    : editMode && isLastStep
      ? '저장하기'
      : '다음';

  return (
    <div class="page page--add-info">
      <header class="title">
        <NavBack onBack={handleBack} />
        <span class="nav-title">{editMode ? '정보 수정' : '추가정보 입력'}</span>
      </header>

      <div class="step-indicator">
        <span class="step-label">
          <b>{step}</b> / {INFO_TOTAL_STEPS}
        </span>
        <div class="step-bar">
          <div class="step-bar-fill" style={{ width: `${(step / INFO_TOTAL_STEPS) * 100}%` }} />
        </div>
      </div>

      <main class="add-info-main">
        <div class="add-info-body">
          {step === 1 && (
            <>
              <h1 class="add-info-title">
                {editMode ? (
                  <>
                    생년월일과 성별을
                    <br />
                    수정할 수 있어요
                  </>
                ) : (
                  <>
                    생년월일과 성별을
                    <br />
                    알려주세요
                  </>
                )}
              </h1>

              <div class="form-row">
                <div class="input-wrap">
                  <div class="date-inputs">
                    <input
                      class="form-input form-input--year"
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="년도"
                      value={year}
                      onInput={numericInput(setYear, 4)}
                    />
                    <input
                      class="form-input form-input--month"
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      placeholder="월"
                      value={month}
                      onInput={numericInput(setMonth, 2)}
                    />
                    <input
                      class="form-input form-input--day"
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      placeholder="일"
                      value={day}
                      onInput={numericInput(setDay, 2)}
                    />
                  </div>
                </div>
                <div class="gender-wrap">
                  <button
                    type="button"
                    class={gender === 'M' ? 'gender-btn active' : 'gender-btn'}
                    onClick={() => setGender('M')}
                  >
                    남
                  </button>
                  <button
                    type="button"
                    class={gender === 'F' ? 'gender-btn active' : 'gender-btn'}
                    onClick={() => setGender('F')}
                  >
                    여
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 class="add-info-title">
                {editMode ? (
                  <>
                    휴대폰 번호를
                    <br />
                    변경할 수 있어요
                  </>
                ) : (
                  <>
                    휴대폰 번호를
                    <br />
                    입력해 주세요
                  </>
                )}
              </h1>

              <div class="form-row">
                <div class="input-wrap">
                  {/*
                    인증 버튼을 따로 두지 않는다. 아래 적립 버튼이
                    발송 -> 확인 두 단계를 맡는다 (handleMobileStep).
                  */}
                  <div class="phone-inputs">
                    <input
                      class="form-input form-input--phone1"
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      placeholder="010"
                      value={phone1}
                      disabled={mobileVerified}
                      onInput={numericInput(setPhone1, 3, phone2Ref)}
                    />
                    <span class="input-dash">-</span>
                    <input
                      ref={phone2Ref}
                      class="form-input form-input--phone2"
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="1234"
                      value={phone2}
                      disabled={mobileVerified}
                      onInput={numericInput(setPhone2, 4, phone3Ref)}
                    />
                    <span class="input-dash">-</span>
                    <input
                      ref={phone3Ref}
                      class="form-input form-input--phone3"
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="5678"
                      value={phone3}
                      disabled={mobileVerified}
                      onInput={numericInput(setPhone3, 4)}
                    />
                  </div>

                  {/* 인증번호 입력 — 문자를 보낸 뒤에만 보인다 */}
                  {codeSent && !mobileDone && (
                    <div class="phone-code-row">
                      <input
                        ref={codeRef}
                        class="form-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={MOBILE_CODE_LENGTH}
                        placeholder={`인증번호 ${MOBILE_CODE_LENGTH}자리 입력`}
                        value={code}
                        onInput={numericInput(setCode, MOBILE_CODE_LENGTH)}
                      />
                    </div>
                  )}

                  {!codeSent && !mobileDone && (
                    <p class="phone-code-hint">
                      번호를 입력하고 아래 버튼을 누르면 인증번호를 문자로 보내드립니다.
                    </p>
                  )}
                  {codeSent && !mobileDone && expiresIn > 0 && (
                    <p class="phone-code-hint">
                      인증번호를 문자로 보냈습니다. 남은 시간 {formatMinSec(expiresIn)}
                    </p>
                  )}
                  {codeSent && !mobileDone && expiresIn <= 0 && (
                    <p class="phone-code-hint is-error">
                      인증번호가 만료되었습니다. 아래 버튼을 눌러 다시 받아주세요.
                    </p>
                  )}
                  {mobileDone && (
                    <p class="phone-code-hint is-done">
                      {mobileVerified
                        ? '인증이 완료되었습니다.'
                        : '이미 인증된 번호입니다. 번호를 바꾸면 다시 인증해야 합니다.'}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 class="add-info-title">
                {editMode ? (
                  <>
                    마케팅 수신 동의를
                    <br />
                    변경할 수 있어요
                  </>
                ) : (
                  <>
                    마케팅 수신을 동의하시면
                    <br />
                    <span class="point-text">{formatPrice(infoStep(3).amount)}P</span>를 더 받으실
                    수 있어요!
                  </>
                )}
              </h1>

              <div class="consent-wrap">
                <button
                  type="button"
                  class={
                    agreeMarketing && agreeAd
                      ? 'consent-item consent-item--all active'
                      : 'consent-item consent-item--all'
                  }
                  onClick={toggleAll}
                >
                  <span class="consent-check">
                    <CheckIcon />
                  </span>
                  전체 동의
                </button>

                <div class="consent-box">
                  {CONSENT_ITEMS.map((item) => {
                    const checked = item.kind === 'marketing' ? agreeMarketing : agreeAd;
                    const setChecked = item.kind === 'marketing' ? setAgreeMarketing : setAgreeAd;

                    return (
                      <div key={item.kind} class="consent-item consent-item--optional">
                        <button
                          type="button"
                          class={
                            checked ? 'consent-optional-label active' : 'consent-optional-label'
                          }
                          onClick={() => setChecked(!checked)}
                        >
                          <span class="consent-check">
                            <CheckIcon />
                          </span>
                          {item.label}
                        </button>
                        <button
                          type="button"
                          class="consent-view-btn"
                          onClick={() => setTermsPopup(item.kind)}
                        >
                          약관보기
                        </button>
                      </div>
                    );
                  })}
                </div>

                <p class="consent-desc">{CONSENT_DESCRIPTION}</p>
              </div>
            </>
          )}
        </div>

        <div class="add-info-actions">
          <button
            type="button"
            class="btn-submit"
            disabled={!stepValid || submitting || sendingCode || verifyingCode}
            onClick={handleSubmit}
          >
            {sendingCode ? '인증번호 발송중…' : verifyingCode ? '인증 확인중…' : submitLabel}
          </button>
          <button type="button" class="btn-later" onClick={handleLater}>
            {editMode ? '취소' : '나중에 하기'}
          </button>
        </div>
      </main>

      <Footer />

      <ConsentTermsPopup kind={termsPopup} onClose={() => setTermsPopup(null)} />

      {/* 수신 동의 완료 */}
      <div class={agreedAt ? 'popup-overlay show' : 'popup-overlay'}>
        <div class="popup-sheet">
          <div class="popup-body popup-body--warning">
            <h3 class="popup-title">
              {step === 3 ? '수신 동의가 완료되었습니다.' : '추가정보 입력이 완료되었습니다.'}
            </h3>
            <p class="popup-desc">
              {step !== 3
                ? '입력해 주신 정보가 저장되었습니다.'
                : agreeAd
                  ? '문자메시지·카카오톡 메시지·앱 푸시를 통한 광고성 정보 수신에 동의했습니다.'
                  : '마케팅 활용을 위한 개인정보 수집·이용에 동의했습니다.'}
            </p>
            {rewarded > 0 && (
              <div class="popup-point-badge">
                <img src="/images/point.png" alt="" />
                <span>{formatPrice(rewarded)}P가 지급되었습니다.</span>
              </div>
            )}
            <div class="popup-caution">
              <ul class="popup-caution-list">
                {step === 3 && <li>동의 일시: {agreedAt}</li>}
                <li>마이페이지 &gt; 회원정보 수정에서 언제든 수신 동의를 철회할 수 있습니다.</li>
              </ul>
            </div>
          </div>
          <button type="button" class="popup-btn" onClick={() => route(returnTo.current)}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

const CheckIcon: FunctionalComponent = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export default Info;
